import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Title2 } from '@sorare/core/src/atoms/typography';
import { Rank } from '@sorare/core/src/components/lobby/Rank';
import { useIntlContext } from '@sorare/core/src/contexts/intl';

type Props = {
  title: string;
  rank: number;
  points: number;
  iconUrl?: string | null;
  gameWeek: string;
};
const Root = styled(Vertical).attrs({ gap: 0, center: true })`
  padding-top: calc(3 * var(--unit));
  gap: calc(3 * var(--unit));
  height: 100%;
`;
const Stats = styled(Horizontal).attrs({ gap: 0 })`
  width: 100%;
  --gap: var(--unit);
  --separator-size: 1px;
  gap: calc(var(--gap) + var(--separator-size));
`;
const Icon = styled.img`
  width: var(--quadruple-unit);
  margin-bottom: var(--double-unit);
  filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.5));
`;

const Stat = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  flex: 1;
  & span {
    color: var(--c-neutral-600);
    text-transform: uppercase;
    font-size: 12px;
  }
  &:nth-child(n + 2)::before {
    position: absolute;
    content: '';
    left: calc(var(--gap) / -2);
    top: 30%;
    bottom: 20%;
    width: var(--separator-size);
    background: var(--c-neutral-600);
  }
  & sup {
    font-weight: normal;
  }
  justify-content: stretch;
`;
export const RewardHeader = ({
  title,
  rank,
  points,
  iconUrl,
  gameWeek,
}: Props) => {
  const { formatNumber } = useIntlContext();

  return (
    <Root>
      <div>
        {iconUrl && <Icon src={iconUrl} />}
        <Title2>{title}</Title2>
      </div>
      <Stats>
        <Stat>
          <span>
            <FormattedMessage id="RewardHeader.Rank" defaultMessage="Rank" />
          </span>
          <strong>
            <Rank rank={rank} Sup={({ children }) => <sup>{children}</sup>} />
          </strong>
        </Stat>
        <Stat>
          <span>
            <FormattedMessage
              id="RewardHeader.Points"
              defaultMessage="Points"
            />
          </span>
          <strong>{formatNumber(points, { maximumFractionDigits: 2 })}</strong>
        </Stat>
        <Stat>
          <span>
            <FormattedMessage
              id="RewardHeader.Gameweek"
              defaultMessage="Game Week"
            />
          </span>
          <strong>{gameWeek}</strong>
        </Stat>
      </Stats>
    </Root>
  );
};
